import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  axiosWithToast,
  dateToString,
  orderPath,
  reportPath,
} from "../../../Utils";
import { Popup, TWclassNames } from "../../../utils/Div";
import { serviceAgreementDaysUntilNextService } from "./utils";
import { Link, useNavigate } from "react-router-dom";
import {
  createOrderServiceAgreement,
  createReportServiceAgreement,
} from "../../../../api/service/agreement";
import SimplePrompt from "../../../utils/simplePrompt";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import {
  getServiceAgreementNextJobUi,
  validateServiceAgreementCreateOrder,
  validateServiceAgreementCreateReport,
} from "./serviceAgreementNextJob";

export const ServiceAgreementNewReportSection = (props) => {
  const { item } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigateToReport = (data) => {
    navigate(reportPath({ report: data }));
  };

  const navigateToOrder = (data) => {
    navigate(orderPath({ order: data }));
  };

  const createReport = async () => {
    const payload = { id: item?.id };
    axiosWithToast(createReportServiceAgreement(payload), navigateToReport);
  };

  const createOrder = async () => {
    const payload = { id: item?.id };
    axiosWithToast(createOrderServiceAgreement(payload), navigateToOrder);
  };

  const [promptOpen, setPromptOpen] = useState(false);
  const [newOrderPromptOpen, setNewOrderPromptOpen] = useState(false);

  const handleCreateReport = () => {
    if (!validateServiceAgreementCreateReport(item)) return;
    setPromptOpen(true);
  };

  const handleCreateOrder = () => {
    if (!validateServiceAgreementCreateOrder(item)) return;
    setNewOrderPromptOpen(true);
  };

  const ui = getServiceAgreementNextJobUi(item);

  const ctaBase =
    "block bg-blue-400 px-4 py-4 text-center text-sm font-medium text-white hover:text-gray-700 sm:rounded-b-lg";

  return (
    <>
      <section className="" aria-labelledby="applicant-information-title">
        <div className="bg-white shadow sm:rounded-lg ">
          <div
            className={TWclassNames(
              "flex w-full items-center px-4 py-5 sm:px-6",
              serviceAgreementDaysUntilNextService({ agreement: item }).bgClass
            )}
          >
            <div className="block w-[60%] ">
              <h2
                id="applicant-information-title"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                {t("service.agreements.next_job.section_title")}
              </h2>
            </div>
          </div>
          <div className="flex flex-col border-t border-gray-200 px-4 py-5 sm:px-6">
            {!item?.active_report?.id ? (
              <>
                <span>
                  {t("service.agreements.next_job.next_on")}{" "}
                  {dateToString(item?.next_service_date)}
                </span>
                <span className="mt-5">
                  {t("service.agreements.next_job.days_until", {
                    count: item?.days_until_next_service,
                  })}
                </span>
              </>
            ) : (
              <span>{t("service.agreements.next_job.in_progress")}</span>
            )}
          </div>
          <div>
            {ui.kind === "missing_type" ? (
              <div
                type="button"
                className={`${ctaBase} cursor-default opacity-90`}
              >
                {t("service.agreements.actions.missing_type")}
              </div>
            ) : null}

            {ui.kind === "link_report" ? (
              <Link
                to={reportPath({ report: ui.report })}
                className={`${ctaBase} cursor-pointer`}
              >
                {t("service.agreements.actions.go_to_report")}
              </Link>
            ) : null}

            {ui.kind === "create_report" ? (
              <div
                type="button"
                role="button"
                tabIndex={0}
                onClick={() => !ui.disabled && handleCreateReport()}
                onKeyDown={(e) => {
                  if (
                    (e.key === "Enter" || e.key === " ") &&
                    !ui.disabled
                  ) {
                    handleCreateReport();
                  }
                }}
                className={TWclassNames(
                  ctaBase,
                  ui.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                )}
              >
                <span className="flex justify-between">
                  <span>{t("service.agreements.actions.create_report")}</span>
                  <Popup
                    content={t("service.agreements.actions.hint_create_report")}
                  >
                    <ExclamationCircleIcon className="h-5 w-5" />
                  </Popup>
                </span>
              </div>
            ) : null}

            {ui.kind === "create_order" ? (
              <div
                type="button"
                role="button"
                tabIndex={0}
                onClick={() => !ui.disabled && handleCreateOrder()}
                onKeyDown={(e) => {
                  if (
                    (e.key === "Enter" || e.key === " ") &&
                    !ui.disabled
                  ) {
                    handleCreateOrder();
                  }
                }}
                className={TWclassNames(
                  ctaBase,
                  ui.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                )}
              >
                <span className="flex justify-between">
                  <span>{t("service.agreements.actions.create_order")}</span>
                  <Popup
                    content={t("service.agreements.actions.hint_create_order")}
                  >
                    <ExclamationCircleIcon className="h-5 w-5" />
                  </Popup>
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <SimplePrompt
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        onAccept={() => createReport()}
        title={t("service.agreements.actions.prompt_report_title")}
        description={t("service.agreements.actions.prompt_report_body")}
      />

      <SimplePrompt
        open={newOrderPromptOpen}
        onClose={() => setNewOrderPromptOpen(false)}
        onAccept={() => createOrder()}
        title={t("service.agreements.actions.prompt_order_title")}
        description={t("service.agreements.actions.prompt_order_body")}
      />
    </>
  );
};
